import { useEffect, useRef } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { shell } from "@/lib/platform";

/**
 * The Android back button.
 *
 * Left alone it closes the app from wherever you are, which loses a half-typed invoice. It
 * should go back a page instead, and only leave the app from the dashboard.
 */
export function useAndroidBackButton(): void {
  const navigate = useNavigate();
  const { pathname } = useLocation();
  // The listener is registered once; it reads the page you are on now, not the one you were
  // on when it was registered.
  const current = useRef(pathname);
  current.current = pathname;

  useEffect(() => {
    if (shell() !== "android") return;

    let stop: (() => void) | undefined;
    let cancelled = false;

    void import("@capacitor/app").then(async ({ App }) => {
      const handle = await App.addListener("backButton", () => {
        if (current.current === "/") {
          void App.exitApp();
        } else {
          void navigate(-1);
        }
      });
      if (cancelled) {
        void handle.remove();
      } else {
        stop = () => void handle.remove();
      }
    });

    return () => {
      cancelled = true;
      stop?.();
    };
  }, [navigate]);
}
